import React from "react"
import clsx from "clsx"
import { ICandidatSuiviDocumentModel, ICandidatSuiviModel } from "@common-models/*"
import {
     CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE,
     CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT,
     CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CTS,
     CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE,
     CONST_CANDIDATSUIVI_TYPE_MESSAGE_REPONDEUR,
     CONST_CANDIDATSUIVI_TYPE_VISIO,
} from "@common-constants/*"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import CandidatSuiviDmsBadges from "./CandidatSuiviDmsBadges"
import CandidatSuiviFeelingAction from "./CandidatSuiviFeelingAction"

interface Props {
     item: ICandidatSuiviModel
     isLast: boolean
     isAuthor: boolean
     onEdit: (item: ICandidatSuiviModel) => void
     onViewDoc: (doc: ICandidatSuiviDocumentModel) => void
     onUpdateFeeling?: (item: ICandidatSuiviModel, feeling: string | null, comment?: string) => void
}

const CandidatSuiviTimelineItem = ({ item, isLast, isAuthor, onEdit, onViewDoc, onUpdateFeeling }: Props) => {
     const isPhoneInterview = item.type === CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE

     // Determine if the feeling system should be displayed based on the interview type
     const isFeelingAllowed = [CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE, CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT, CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CTS].includes(
          item.type!
     )

     // Configuration des icônes
     const getIconConfig = () => {
          switch (item.type) {
               case CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_TELEPHONIQUE:
                    return { icon: "fa-phone-alt", color: "primary" }
               case CONST_CANDIDATSUIVI_TYPE_MESSAGE_REPONDEUR:
                    return { icon: "fa-phone-slash", color: "warning" }
               case CONST_CANDIDATSUIVI_TYPE_VISIO:
                    return { icon: "fa-video", color: "info" }
               case CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CLIENT:
                    return { icon: "fa-user-check", color: "success" }
               case CONST_CANDIDATSUIVI_TYPE_ENTRETIEN_CTS:
                    return { icon: "fa-eye", color: "primary" }
               case CONST_CANDIDATSUIVI_TYPE_BLACKLIST_NON_RECOMMANDE:
                    return { icon: "fa-ban", color: "danger" }
               default:
                    return { icon: "fa-sticky-note", color: "gray-500" }
          }
     }

     const config = getIconConfig()

     return (
          <div className="d-flex mb-8 position-relative">
               {/* Axe de la Timeline */}
               <div className="d-flex flex-column align-items-center me-5" style={{ minWidth: "50px" }}>
                    {!isLast && <div className="position-absolute h-100 bg-gray-300" style={{ width: "2px", left: "24px", top: "10px", zIndex: 0 }} />}
                    <div className="symbol symbol-50px symbol-circle z-index-1 border border-4 border-body bg-body">
                         <div className={clsx("symbol-label fs-3 shadow-sm", `bg-light-${config.color} text-${config.color}`)}>
                              <i className={clsx("fs-3 fas", config.icon)} />
                         </div>
                    </div>
               </div>

               {/* Contenu de la Note */}
               <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                         <div>
                              <span className={clsx("fw-boldest fs-6 me-2", `text-${config.color}`)}>{item.typeLabel || "Note de suivi"}</span>
                              <span className="text-gray-500 fs-7 fw-semibold">{item.date_suivi?.format("DD MMM YYYY [à] HH:mm")}</span>
                         </div>
                         <div className="d-flex align-items-center">
                              <div className="symbol symbol-25px symbol-circle me-2">
                                   <div className="symbol-label bg-light-danger text-danger fs-8 fw-boldest">{item.personnel?.prenomNom?.charAt(0)}</div>
                              </div>
                              <span className="text-gray-700 fw-bold fs-7">{item.personnel?.prenomNom}</span>
                         </div>
                    </div>

                    <div className="card card-custom border border-gray-300 position-relative bg-white shadow-none rounded-4">
                         <div className="card-body p-5">
                              {/* Bouton Edition */}
                              {isAuthor && (
                                   <button onClick={() => onEdit(item)} className="btn btn-icon btn-sm btn-active-light-primary position-absolute top-0 end-0 m-2">
                                        <i className="fas fa-pencil-alt fs-7 text-gray-400" />
                                   </button>
                              )}

                              {/* Zone des Badges de Contexte (Offre et Client) */}
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                   {/* Affichage de l'offre associée (Subtil) */}
                                   {item.annonce?.intitule && (
                                        <span className="badge badge-light-primary border border-primary border-opacity-10 py-2 px-3 fs-9 fw-boldest text-uppercase">
                                             <i className="fas fa-briefcase text-primary fs-9 me-2"></i>
                                             {item.annonce.intitule}
                                        </span>
                                   )}

                                   {/* Affichage du Client (Subtil) - Nouveau champ */}
                                   {item.client?.nom && (
                                        <span className="badge badge-light-success border border-success border-opacity-10 py-2 px-3 fs-9 fw-boldest text-uppercase">
                                             <i className="fas fa-building text-success fs-9 me-2"></i>
                                             {item.client.nom}
                                        </span>
                                   )}
                              </div>

                              {/* Commentaire de l'action */}
                              <div
                                   className="fs-6 text-gray-800 mb-2"
                                   style={{ whiteSpace: "pre-wrap" }}
                                   dangerouslySetInnerHTML={{ __html: item.commentaire || "<em class='text-muted'>Aucun commentaire saisi.</em>" }}
                              />

                              {/* Badges DMS Spécifiques (Only for Phone Interview) */}
                              {isPhoneInterview && item.candidatSuiviSMDs?.[0] && (
                                   <div className="mt-4 pt-3 border-top-dashed border-gray-100">
                                        <CandidatSuiviDmsBadges dms={item.candidatSuiviSMDs[0]} />
                                   </div>
                              )}

                              {/* Actions de Feeling (Allowed for Phone, Client, and CTS interviews) */}
                              {isFeelingAllowed && onUpdateFeeling && (
                                   <CandidatSuiviFeelingAction item={item} onUpdateFeeling={(feeling, comment) => onUpdateFeeling(item, feeling, comment)} />
                              )}

                              {/* Liste des Documents joints */}
                              {item.candidatSuiviDocuments && item.candidatSuiviDocuments.length > 0 && (
                                   <div className="d-flex flex-wrap gap-2 mt-5">
                                        {item.candidatSuiviDocuments.map((doc, idx) => (
                                             <div
                                                  key={idx}
                                                  onClick={() => onViewDoc(doc)}
                                                  className="d-flex align-items-center px-4 py-2 border border-gray-200 rounded-3 bg-light-white cursor-pointer hover-elevate-up transition-all"
                                             >
                                                  <span
                                                       className={clsx(
                                                            FileAndDriveHelper.getFontAwesomeClassAndColorAccordingToFileMimeType(doc.googleDriveMimeType!),
                                                            "fs-4 me-2"
                                                       )}
                                                  />
                                                  <span className="fw-boldest text-gray-700 fs-9 text-uppercase">Consulter Document</span>
                                             </div>
                                        ))}
                                   </div>
                              )}
                         </div>
                    </div>
               </div>
          </div>
     )
}

export default CandidatSuiviTimelineItem
